import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation } from 'react-query';
import * as yup from 'yup';
import { Dialog, Typography, FormControlLabel, Radio, RadioGroup, FormControl, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Checkbox } from 'shared/ui/Checkbox';
import { getPartners } from 'entities/Partners/api';
import { retryImportFile } from 'entities/ImportSessions/api';
import { FileType } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { useCustomModelStore } from 'widgets/customModel/state';
import { useFileEditorStore } from 'store/file-editor';
import FormAutocomplete from 'shared/ui/autocomplete/form-autocomplete';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { AutocompleteOption } from 'shared/ui/autocomplete/Autocomplete';
import { useCustomSearchParams } from 'shared/hooks/use-custom-search';
import { Loader } from 'shared/ui/Loader';
import { GeneralPartnerType } from 'entities/Partners/types';

const PartnersSchema = yup.object({
	partnerId: yup
		.object({
			value: yup.string().notRequired(),
			label: yup.string().notRequired(),
		})
		.nullable(),
	receiverId: yup.string().nullable().notRequired(),
});

interface PartnersModalPropsType {
	open: boolean;
	handleClose: () => void;
	file?: FileType;
}

const PartnersModal: FC<PartnersModalPropsType> = ({ open, handleClose, file }) => {
	const { fileId } = useParams();
	const { t } = useTranslation();
	const { searchQueries, changeSearchQueries } = useCustomSearchParams();
	const [selectedPartner, setSelectedPartner] = useState<AutocompleteOption | null>(null);
	const [usePartnersAssertions, setUsePartnersAssertions] = useState(true);
	const [selectManuallyPartner, setSelectManuallyPartner] = useState(false);
	const { setFileDetailsV2 } = useFileEditorStore();
	const { setCustomFileDetails } = useCustomModelStore();

	const isPartnerCandidatesVisible = useMemo(
		() => file?.partnerCandidates?.length && !file.partnerId,
		[file],
	);

	const partnerIds = useMemo(() => {
		return file?.partnerCandidates || (file?.partnerId && [file?.partnerId]) || undefined;
	}, [file]);

	const hasLoadedPartnersOnceRef = useRef(false);

	const renderPartnerDetails = (item: GeneralPartnerType) => (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
			<Typography sx={{ whiteSpace: 'normal' }}>{item.name}</Typography>
			{item.address && (
				<Box
					sx={{
						display: 'flex',
						gap: 0.5,
						minWidth: 0,
						whiteSpace: 'normal',
					}}
				>
					<Typography variant="body2" sx={{ flexShrink: 0 }} color={ColorPalette.baliHai}>
						{t('partners.labels.address')}
					</Typography>

					<Typography variant="body2">{item.address}</Typography>
				</Box>
			)}
			{item.erpCode && (
				<Box
					sx={{
						display: 'flex',
						gap: 0.5,
						minWidth: 0,
						whiteSpace: 'normal',
					}}
				>
					<Typography variant="body2" sx={{ flexShrink: 0 }} color={ColorPalette.baliHai}>
						{t('partners.labels.erpCode')}
					</Typography>

					<Typography variant="body2">{item.erpCode}</Typography>
				</Box>
			)}
		</Box>
	);

	const {
		data: partnersResponseData,
		isLoading: getPartnersIsLoading,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useInfiniteQuery({
		queryKey: ['get/partners', partnerIds, searchQueries.search, searchQueries.perPage],
		queryFn: ({ pageParam = 1 }) =>
			getPartners({
				type: null,
				partnerIds: typeof searchQueries.search === 'string' ? undefined : partnerIds,
				search: searchQueries.search,
				perPage: searchQueries.perPage,
				page: pageParam,
			}),
		getNextPageParam: (lastPage, allPages) => {
			const fetchedCount = allPages.reduce((sum, page) => sum + page.data.results.length, 0);
			return fetchedCount < lastPage.data.totalCount ? lastPage.data.page + 1 : undefined;
		},
		onSuccess: () => {
			hasLoadedPartnersOnceRef.current = true;
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
		enabled: open,
	});

	const partnersList = useMemo(
		() => partnersResponseData?.pages.flatMap((page) => page.data.results) ?? [],
		[partnersResponseData],
	);

	const partnersListForAutocomplete = useMemo(
		() =>
			partnersList.map((item) => ({
				value: item._id,
				customLabel: renderPartnerDetails(item),
				label: item.name,
			})),
		[partnersList],
	);

	const defaultValueForPartners = useMemo(() => {
		const partner = partnersList.find((item) => item._id === file?.partnerId);
		return partner ? { value: partner?._id, label: partner?.name } : undefined;
	}, [partnersList, file]);

	const isInitialPartnersLoading = getPartnersIsLoading && !hasLoadedPartnersOnceRef.current;

	const selectedPartnerObject = useMemo(
		() => partnersList.find((item) => item._id === (selectedPartner?.value ?? file?.partnerId)),
		[partnersList, selectedPartner, file?.partnerId],
	);

	const destinationOptions = useMemo(() => {
		if (!selectedPartnerObject) return [];
		const fromDestinations = (selectedPartnerObject.destinations ?? []).map((d) => ({
			receiverName: d.receiverName,
			receiverAddress: d.receiverAddress,
			receiverId: d.receiverId,
		}));
		return fromDestinations;
	}, [selectedPartnerObject]);

	const defaultReceiverId = useMemo(() => {
		if (!file?.receiverId) return '';
		return file.receiverId;
	}, [file?.receiverId]);

	useEffect(() => {
		if (isPartnerCandidatesVisible) {
			setSelectManuallyPartner(true);
		} else {
			setSelectManuallyPartner(false);
		}
	}, [isPartnerCandidatesVisible]);

	const { mutate, isLoading } = useMutation({
		mutationKey: ['patch/files/recognition-result'],
		mutationFn: retryImportFile,
	});

	const handleSubmit = (data: yup.InferType<typeof PartnersSchema>) => {
		mutate(
			{
				id: file?._id,
				data: {
					recognizedLabels: file?.recognizedLabels || [],
					assertionSetsId:
						selectedPartnerObject?.assertionSetsId && usePartnersAssertions
							? selectedPartnerObject?.assertionSetsId
							: file?.assertionSetsId || null,
					partnerId: data?.partnerId?.value || null,
					receiverId: data?.receiverId || undefined,
					isPreview: true,
				},
			},
			{
				onSuccess: (response) => {
					enqueueSnackbar({
						message: t('partners.snackbar.success_settings_applying'),
						variant: 'success',
					});
					if (fileId) {
						setFileDetailsV2(response.data);
					} else {
						setCustomFileDetails(response.data);
					}
					handleClose();
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			PaperProps={{
				title: t('partners.title') as string,
			}}
		>
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '34px' }}>
				{t('partners.select_partner')}
			</Typography>

			{isInitialPartnersLoading ? (
				<Loader />
			) : (
				<Formik
					initialValues={{
						partnerId: defaultValueForPartners || null,
						receiverId: defaultReceiverId,
					}}
					validationSchema={PartnersSchema}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ values, setFieldValue }) => (
						<Form>
							{selectManuallyPartner ? (
								<FormControl
									sx={{ maxHeight: '250px', width: '100%', overflow: 'auto', paddingX: 3 }}
								>
									<RadioGroup
										name="partnerId"
										value={values.partnerId?.value}
										onChange={(e) =>
											setFieldValue('partnerId', {
												value: e.target.value,
												label: '',
											})
										}
									>
										{partnersList.map((item) => (
											<FormControlLabel
												key={item._id}
												value={item._id}
												control={<Radio size="small" />}
												label={renderPartnerDetails(item)}
												sx={{ alignItems: 'flex-start', marginY: 1 }}
											/>
										))}
									</RadioGroup>
								</FormControl>
							) : (
								<>
									<FormAutocomplete
										name="partnerId"
										defaultValue={defaultValueForPartners}
										options={partnersListForAutocomplete}
										label={t('partners.labels.client_supplier') as string}
										placeholder={t('partners.placeholder.client_supplier') as string}
										onSearch={(value) => changeSearchQueries({ search: value })}
										onChange={(value) => {
											setSelectedPartner(value as AutocompleteOption);
											setFieldValue(
												'partnerId',
												value ? (value as AutocompleteOption).value : null,
											);
											setFieldValue('receiverId', null);
										}}
										sx={{ margin: '0' }}
										loadMore={Boolean(hasNextPage)}
										onFetchMore={() => fetchNextPage()}
										loading={getPartnersIsLoading || isFetchingNextPage}
									/>
									{destinationOptions.length > 0 && (
										<FormSelect
											name="receiverId"
											label={t('partners.labels.select_receiver') as string}
											placeholder={t('partners.placeholder.select_receiver')}
											value={values.receiverId ?? ''}
											onSelect={(value) => setFieldValue('receiverId', value || null)}
											onClear={() => setFieldValue('receiverId', null)}
											options={destinationOptions.map((opt) => ({
												value: opt.receiverId,
												label: opt.receiverId,
												customLabel: (
													<Box
														sx={{
															display: 'flex',
															flexDirection: 'column',
															gap: 0.5,
															minWidth: 0,
														}}
													>
														<Box
															sx={{
																display: 'flex',
																gap: 0.5,
																minWidth: 0,
																whiteSpace: 'normal',
															}}
														>
															<Typography
																variant="body2"
																color={ColorPalette.baliHai}
																sx={{ flexShrink: 0 }}
															>
																{t('partners.labels.receiver_id')}:
															</Typography>
															<Typography variant="body2">
																{opt.receiverId}
															</Typography>
														</Box>
														<Box
															sx={{
																display: 'flex',
																gap: 0.5,
																minWidth: 0,
																whiteSpace: 'normal',
															}}
														>
															<Typography
																variant="body2"
																sx={{ flexShrink: 0 }}
																color={ColorPalette.baliHai}
															>
																{t('partners.labels.receiver_name')}:
															</Typography>

															<Typography variant="body2">
																{opt.receiverName}
															</Typography>
														</Box>

														{opt.receiverAddress && (
															<Box
																sx={{
																	display: 'flex',
																	gap: 0.5,
																	minWidth: 0,
																	whiteSpace: 'normal',
																}}
															>
																<Typography
																	variant="body2"
																	color={ColorPalette.baliHai}
																	sx={{ flexShrink: 0 }}
																>
																	{t('partners.labels.receiver_address')}:
																</Typography>
																<Typography variant="body2">
																	{opt.receiverAddress}
																</Typography>
															</Box>
														)}
													</Box>
												),
											}))}
											sx={{ marginTop: 3, marginBottom: 0 }}
										/>
									)}
								</>
							)}

							{isPartnerCandidatesVisible && (
								<Typography
									variant={'body2'}
									color={ColorPalette.royalBlue}
									onClick={() => setSelectManuallyPartner(!selectManuallyPartner)}
									sx={{
										cursor: 'pointer',
										margin: '20px 0',
									}}
								>
									{selectManuallyPartner
										? t('partners.select_manually')
										: t('partners.check_proposals')}
								</Typography>
							)}

							{selectedPartnerObject?.assertionSetsId && (
								<FormControlLabel
									control={
										<Checkbox
											checked={usePartnersAssertions}
											onChange={() => {
												setUsePartnersAssertions(!usePartnersAssertions);
											}}
										/>
									}
									label={
										<Typography
											variant={'body2'}
											color={ColorPalette.baliHai}
											sx={{ marginLeft: '10px' }}
										>
											{t('partners.assertions_checkbox')}
										</Typography>
									}
									sx={{ margin: '0', marginTop: 2 }}
								/>
							)}
							<ActionsForDialog
								cancelLabel={t('shared_texts.cancel')}
								cancelAction={handleClose}
								applyLabel={t('shared_texts.continue')}
								applyActionDisabled={isLoading}
								sx={{ marginTop: 5 }}
							/>
						</Form>
					)}
				</Formik>
			)}
		</Dialog>
	);
};

export default PartnersModal;
